/**
 * Camera controller — a pure view concern, kept out of the simulation. Follows
 * the player and (when enabled) zooms out as the player grows so incoming
 * threats stay visible, and optionally lets the player manually zoom within
 * hard bounds. All behaviour reads from config; manual zoom never touches the
 * simulation — the controller only mutates its own view state.
 *
 * Zoom target resolution:
 *   - manual off                  -> auto-zoom(size), clamped to [minZoom,maxZoom]
 *   - manual on, mode 'relative'  -> auto-zoom(size) * clamp(factor, nudgeMin, nudgeMax)
 *   - manual on, mode 'absolute'  -> clamp(baseZoom * factor, manualMin, manualMax)
 * where `factor` is the accumulated player zoom input (1 = neutral). In
 * 'relative' mode the bound is a multiplier AROUND the size-based baseline, so
 * the nudge is always live (it can't go dead at large sizes the way a flat
 * absolute clamp does). The factor is clamped each frame so it can't wind up.
 */
import type { GameConfig } from '../config/config';
import { clamp, lerpScalar } from '../core/math/vec2';
import type { CameraState } from './renderer';

export interface CameraOptions {
  /** FEATURES.cameraZoom — size-based auto zoom. */
  autoZoom: boolean;
  /** FEATURES.manualZoom — player-driven scroll/pinch zoom. */
  manual: boolean;
}

export class CameraController {
  readonly state: CameraState;
  private readonly cfg: GameConfig;
  private readonly autoZoomEnabled: boolean;
  private readonly manualEnabled: boolean;
  /** Player's accumulated manual zoom multiplier (1 = neutral). */
  private manualFactor = 1;

  constructor(cfg: GameConfig, opts: CameraOptions) {
    this.cfg = cfg;
    this.autoZoomEnabled = opts.autoZoom;
    this.manualEnabled = opts.manual;
    this.state = { x: cfg.world.width / 2, y: cfg.world.height / 2, zoom: cfg.camera.baseZoom };
  }

  /** Raw size-based zoom (unclamped) — the relative-mode baseline. */
  private autoZoomRaw(playerSize: number): number {
    const c = this.cfg.camera;
    if (!this.autoZoomEnabled) return c.baseZoom;
    return c.baseZoom * Math.pow(c.referenceSize / playerSize, c.zoomExponent);
  }

  /** Auto zoom clamped to the auto bounds — the behaviour when manual is off. */
  private autoZoom(playerSize: number): number {
    const c = this.cfg.camera;
    return clamp(this.autoZoomRaw(playerSize), c.minZoom, c.maxZoom);
  }

  /** The zoom the camera is currently easing toward. */
  private computeTarget(playerSize: number): number {
    if (!this.manualEnabled) return this.autoZoom(playerSize);
    const c = this.cfg.camera;
    if (c.manualZoomMode === 'relative') {
      // Auto-zoom sets the baseline (size-based); the manual factor nudges
      // around it within the nudge range. Multiplicative bound => always live.
      const f = clamp(this.manualFactor, c.manualNudgeMin, c.manualNudgeMax);
      return this.autoZoom(playerSize) * f;
    }
    // Absolute: manual sets the zoom directly within the hard bounds (auto-zoom
    // is ignored — a neutral factor sits at baseZoom regardless of size).
    return clamp(c.baseZoom * this.manualFactor, c.manualMinZoom, c.manualMaxZoom);
  }

  /** Keep the factor from winding up beyond its usable range each frame. */
  private settleFactor(playerSize: number): void {
    if (!this.manualEnabled) return;
    const c = this.cfg.camera;
    if (c.manualZoomMode === 'relative') {
      this.manualFactor = clamp(this.manualFactor, c.manualNudgeMin, c.manualNudgeMax);
    } else if (c.baseZoom > 0) {
      this.manualFactor = this.computeTarget(playerSize) / c.baseZoom;
    }
  }

  /** Apply a multiplicative manual zoom delta (>1 zooms in, <1 zooms out). */
  nudgeManualZoom(deltaMultiplier: number): void {
    if (!this.manualEnabled || !(deltaMultiplier > 0) || deltaMultiplier === 1) return;
    // Keep the factor in a sane range; settleFactor() pins it to the clamp each frame.
    this.manualFactor = clamp(this.manualFactor * deltaMultiplier, 1e-3, 1e3);
  }

  /** Snap instantly to a target (used on (re)spawn to avoid a long pan). */
  snapTo(x: number, y: number, playerSize: number): void {
    this.state.x = x;
    this.state.y = y;
    this.settleFactor(playerSize);
    this.state.zoom = this.computeTarget(playerSize);
  }

  /** Ease toward the target each frame. `targetX/Y` is the (interpolated) player position. */
  update(targetX: number, targetY: number, playerSize: number, dtSec: number): void {
    const c = this.cfg.camera;
    this.settleFactor(playerSize);
    const followT = clamp(c.followLerp * dtSec, 0, 1);
    const zoomT = clamp(c.zoomLerp * dtSec, 0, 1);
    this.state.x = lerpScalar(this.state.x, targetX, followT);
    this.state.y = lerpScalar(this.state.y, targetY, followT);
    this.state.zoom = lerpScalar(this.state.zoom, this.computeTarget(playerSize), zoomT);
  }
}
